import LoginImage from '@assets/images/login.jpg';
import { LoadingScreen } from '@components/LoadingScreen';
import { LocaleToggle } from '@modules/application/components/LocaleToggle';
import { TenantBrandingHeader } from '@modules/tenants/components/TenantBrandingHeader';
import { TenantThemeContext } from '@modules/tenants/components/TenantThemeContextProvider';
import { Button, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { useGetMe } from '@utils/useGetMe';
import { useValidationRules } from '@utils/useValidationRules';
import { Controller, useForm } from 'react-hook-form';
import { useContext, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useNotification } from '../../../utils/useNotification';
import { useAuthResultMessageFormatter } from '../../../utils/getAuthResultMessage';
import { useResponsive } from '@utils/useResponsive';

interface CreateAccountFormValues {
  newPassword: string;
  confirmPassword: string;
}

export const CreateAccountPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const formatAuthResultMessage = useAuthResultMessageFormatter();
  const { tenantId } = useParams();
  const [searchParams] = useSearchParams();
  const tenantContext = useContext(TenantThemeContext);
  const { notifyFailure } = useNotification();
  const { isFetching } = useGetMe();
  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';

  const { control, handleSubmit, watch } = useForm<CreateAccountFormValues>({
    defaultValues: { newPassword: '', confirmPassword: '' }
  });
  const newPassword = watch('newPassword');
  const formRef = useRef<HTMLFormElement>(null);
  const { notEmpty, validatePassword } = useValidationRules();
  const { isMobile } = useResponsive();
  useEffect(() => {
    const successParam = searchParams.get('success');
    const code = searchParams.get('code');
    if (code != null && successParam !== 'true') {
      const message = formatAuthResultMessage(false, code);
      if (message) notifyFailure(message);
    }
  }, [formatAuthResultMessage, searchParams, notifyFailure]);

  if (isFetching) return <LoadingScreen />;
  if (tenantId == null || !email || !token) return null;

  const submitFormWhenValid = () => {
    formRef.current?.submit();
  };

  return (
    <Stack
      sx={{
        backgroundImage: `url(${tenantContext.loginBackgroundUrl ?? LoginImage})`,
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        flexGrow: 1
      }}
      flex={1}
      justifyContent="center"
      alignItems="center"
    >
      <Container maxWidth="sm"  disableGutters sx={{ height: { xs: '100%', sm: 'auto' } }}> 
    <Paper elevation={isMobile ? 0 : 4}
    sx={{ 
      height: { xs: '100%', sm: 'auto' }, 
      width: { xs: '100%', sm: 'auto' },
    }}
    >
        <Stack p={4} gap={4} justifyContent="center" alignItems="center">
          <Stack direction="row" alignSelf="stretch" justifyContent="flex-end">
            <LocaleToggle />
          </Stack>
          <TenantBrandingHeader />
          <Typography variant="h5" color="primary.main">
            {formatMessage({ id: 'Create your account' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {formatMessage({ id: 'Set your password to access the full project.' })}
          </Typography>
          <form
            ref={formRef}
            method="POST"
            action={`/${tenantId}/api/Auth/CreateAccount`}
            onSubmit={handleSubmit(submitFormWhenValid)}
          >
            <Stack spacing={2} width="100%" maxWidth={360}>
              <input type="hidden" name="email" value={email} readOnly />
              <input type="hidden" name="token" value={token} readOnly />
              <Controller
                name="newPassword"
                control={control}
                rules={{ validate: { notEmpty, validatePassword } }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    type="password"
                    label={formatMessage({ id: 'New password' })}
                    error={!!error}
                    helperText={error?.message}
                    autoComplete="new-password"
                    slotProps={{ input: { name: 'newPassword' } }}
                  />
                )}
              />
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  validate: value =>
                    value === newPassword || formatMessage({ id: 'Passwords do not match' })
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    type="password"
                    label={formatMessage({ id: 'Confirm password' })}
                    error={!!error}
                    helperText={error?.message}
                    autoComplete="new-password"
                  />
                )}
              />
              <Button type="submit" variant="contained" fullWidth>
                {formatMessage({ id: 'Create account' })}
              </Button>
            </Stack>
          </form>
          <Link to={`/${tenantId}/login`}>
            <Button variant="text" size="small">
              {formatMessage({ id: 'Go to login' })}
            </Button>
          </Link>
        </Stack>
      </Paper>
    </Container>
    </Stack>
  );
};
